/**
 * Where a credential comes from.
 *
 * ⛔ THE STORE WAS DECLARED AND NEVER READ. `secrets.store` has been in the schema since the first
 * commit, the configuration reference documents its precedence, `sibyl init` emits it — and every
 * call site resolved credentials straight out of `process.env`. A key written to the documented
 * location was silently ignored, and the tool reported "no credential found under the name
 * ANTHROPIC_API_KEY" while looking at a file that contained exactly that. Measured 2026-09-15 on a
 * real installation. This module is the reader that should have existed.
 *
 * ⛔ NOTHING HERE EVER RETURNS, LOGS OR ECHOES A VALUE ANYWHERE BUT TO ITS CALLER. Errors name the
 * FILE and the KEY NAME, never the secret. A credential that reaches an error message reaches a log,
 * and a log is forever.
 *
 * ⚠️ Precedence is environment first, then the store — matching the configuration reference, and
 * deliberately in that order: an operator exporting a variable for one run must be able to override
 * a stored key without editing a file they may not be able to write.
 */

import { readFileSync, statSync } from "node:fs"
import { expandPath } from "./load.js"

export interface ResolvedCredential {
  readonly name: string
  readonly value: string
  /** Where it came from, so a banner can say so without printing the value. */
  readonly source: "environment" | "store"
}

export class SecretsError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "SecretsError"
  }
}

/**
 * Read `KEY=VALUE` lines from the credential store.
 *
 * ⛔ REFUSES A FILE ANY OTHER USER CAN READ. A credential file at mode 644 is a credential shared
 * with every account on the machine, and silently using it would make sibyl the reason a key
 * leaked. The check is on the file, not on the directory, because the file is what holds the value.
 *
 * ⚠️ Returns an empty map when the file is absent. Absence is the ordinary case — most installations
 * use the environment — and is not an error.
 */
export function loadCredentialStore(path: string, opts: { requireOwnerOnly?: boolean } = {}): Map<string, string> {
  const resolved = expandPath(path)
  const out = new Map<string, string>()

  let mode: number
  try {
    mode = statSync(resolved).mode
  } catch {
    return out
  }

  // 0o077 is group+other. Anything set there means somebody else can read the key.
  if ((opts.requireOwnerOnly ?? true) && (mode & 0o077) !== 0) {
    throw new SecretsError(
      `${resolved} is readable by other users on this machine (mode ${(mode & 0o777).toString(8)}). ` +
        `A credential file must be owner-only: chmod 600 ${resolved}`,
    )
  }

  let text: string
  try {
    text = readFileSync(resolved, "utf8")
  } catch (e) {
    throw new SecretsError(`${resolved} could not be read: ${e instanceof Error ? e.message : String(e)}`)
  }

  for (const raw of text.split("\n")) {
    const line = raw.trim()
    if (line === "" || line.startsWith("#")) continue
    const eq = line.indexOf("=")
    // ⚠️ A malformed line is skipped rather than reported with its content: the content is the
    // secret. The KEY of a well-formed line is safe to name; a broken line has no trustworthy key.
    if (eq <= 0) continue
    const key = line.slice(0, eq).trim()
    let value = line.slice(eq + 1).trim()
    // Strip one layer of matching quotes, which is how most people write these files.
    if (value.length >= 2 && ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'")))) {
      value = value.slice(1, -1)
    }
    if (key !== "" && value !== "") out.set(key, value)
  }
  return out
}

/**
 * First name that resolves wins, environment before store.
 *
 * ⭐ Returns WHICH name and WHICH source matched. A run that cannot find a credential must be able
 * to say "I looked for GITHUB_TOKEN and GH_TOKEN, in the environment and in <file>" — a bare "no
 * credential" sends the reader to rotate a key that was never the problem.
 */
export function resolveCredential(
  names: readonly string[],
  env: NodeJS.ProcessEnv,
  store: ReadonlyMap<string, string>,
): ResolvedCredential | undefined {
  for (const name of names) {
    const v = env[name]
    if (typeof v === "string" && v !== "") return { name, value: v, source: "environment" }
  }
  for (const name of names) {
    const v = store.get(name)
    if (typeof v === "string" && v !== "") return { name, value: v, source: "store" }
  }
  return undefined
}

/** The sentence a command prints when it cannot find one. Names every place it looked. */
export function describeMissing(names: readonly string[], storePath: string): string {
  return (
    `no credential found under ${names.join(", ")}. Looked in the environment, then in ` +
    `${expandPath(storePath)}. Set one of those names in either place.`
  )
}

/**
 * Presence and a fingerprint, for anything that displays credential state.
 *
 * ⛔ Never the value. Under 12 characters reports presence only, because four characters of a short
 * value is most of it.
 */
export function credentialHint(value: string | undefined): string {
  if (value === undefined || value === "") return ""
  return value.length >= 12 ? `…${value.slice(-4)}` : "set"
}
